import DefaultLayout from 'admin/components/DefaultLayout';
import { StyledFarmEdit } from './style';
import { useIntl } from 'react-intl';
import { Button, Spinner, Tabs } from '../../../ui';
import { useMemo, useRef, useState } from 'react';
import { BaseInformation } from '../../components/EditFarm/BaseInformation/BaseInformation';
import { useForm } from 'antd/lib/form/Form';
import { Form, message, UploadFile } from 'antd';
import { ContactInformation } from '../../components/EditFarm/ContactInformation/ContactInformation';
import { AdditionalInformation } from '../../components/EditFarm/AdditionalInformation/AdditionalInformation';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { BusinessGallery } from '../../components/EditFarm/BusinessGallery/BusinessGallery';
import { BusinessAddresses } from '../../components/EditFarm/BusinessStructuralUnit/BusinessAddresses';
import { FarmsProps, ImageProps } from '../../../interfaces/BackendModels';
import { Contracts } from './Contracts/Contracts';
import dayjs from 'dayjs';
import { convertToDayJs } from 'utils/timeHelpers';
import { options } from 'config/config';
import Partners from 'admin/components/Partners';
import FarmDeliveries from 'admin/components/FarmDeliveries';
import type { TabsProps } from 'antd';
import { StyledBusinessInformationFooter } from 'admin/pages/BusinessInformation/style';
import { RcFile } from 'antd/es/upload';
import QualityMark from './QualityMark/QualityMark';
import { useAuth } from '../../../hooks/useAuth';

interface EditFarmProps {
  farmId: number;
  setEditFarmDrawer: () => void;
}

export const EditFarm = ({ farmId, setEditFarmDrawer }: EditFarmProps) => {
  const { user } = useAuth();
  const isGlobalAdmin = !!user?.isAdmin;
  const [fileLogo, setFileLogo] = useState<UploadFile[]>([]);
  const [fileGallery, setFileGallery] = useState<UploadFile[]>([]);
  const [isFooterHidden, setIsFooterHidden] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPublicAllowed, setIsPublicAllowed] = useState<boolean>(false);

  const intl = useIntl();
  const [form] = useForm();

  const stableFarmId = useMemo(() => {
    return farmId;
  }, [farmId]);

  const autoSyncTriggered = useRef(false);

  const { isLoading: farmLoading, refetch: refetchFarm } = useQueryApiClient({
    request: {
      url: `/api/v2/farms/${stableFarmId}`,
    },
    enabled: !!stableFarmId,
    onSuccess: (response: FarmsProps) => handleSuccessFarmDataFetch(response),
  });

  const { appendData: submitData, isLoading: isFarmSaving } = useQueryApiClient(
    {
      request: {
        url: '/api/v2/farms/:farmId',
        method: 'POST',
        multipart: true,
      },
      onSuccess: () =>
        message.success({
          content: intl.formatMessage({ id: 'message.success_update_farm' }),
          key: 'farm-save-status',
        }),
      onError: () => {
        message.error({
          content: intl.formatMessage({ id: 'message.error_farm_edit' }),
          key: 'farm-save-status',
        });
      },
    }
  );

  const { appendData: farmsLogoFetch, isLoading: isLogoLoading } =
    useQueryApiClient({
      request: {
        url: '/api/v2/image',
        multipart: true,
        disableOnMount: true,
      },
      onSuccess: (response, passOnSuccess) =>
        handleImageFile(response, passOnSuccess.logoUrl, true),
    });

  const { appendData: farmsGalleryFetch, isLoading: isGalleryLoading } =
    useQueryApiClient({
      request: {
        url: '/api/v2/image',
        multipart: true,
        disableOnMount: true,
      },
      onSuccess: (response, passOnSuccess) =>
        handleImageFile(response, passOnSuccess.galleryUrl, false),
    });

  const { appendData: syncFarm, isLoading: isFarmSyncing } = useQueryApiClient({
    request: {
      url: '/api/v2/farms/:farmId/sync',
      method: 'POST',
      errorWithHeaders: true,
    },
    onSuccess: () => {
      message.success(intl.formatMessage({ id: 'message.success_farm_sync' }));
      refetchFarm();
    },
    onError: (errorResponse) => {
      if (errorResponse?.status === 429) {
        message.warning(intl.formatMessage({ id: 'message.please_wait_time' }));
        return;
      }

      message.error(intl.formatMessage({ id: 'message.error_farm_edit' }));
    },
  });

  const { appendData: autoSyncFarm } = useQueryApiClient({
    request: {
      url: '/api/v2/farms/:farmId/sync',
      method: 'POST',
      disableOnMount: true,
    },
    onSuccess: () => {
      refetchFarm();
    },
  });

  const handleImageFile = (response: any, url: string, isLogo: boolean) => {
    const fileName = url.split('/').pop() || 'unknown_file';

    const file = new File([response], fileName, { type: response.type });

    const rcFile = file as RcFile;

    const blobUrl = URL.createObjectURL(response);

    const fileObj: UploadFile = {
      uid: rcFile.uid,
      name: fileName,
      preview: blobUrl,
      originFileObj: rcFile,
    };

    if (isLogo) {
      setFileLogo([fileObj]);
    } else {
      setFileGallery((prev) => [...prev, fileObj]);
    }
  };

  function triggerAutoSyncIfStale(syncAt: string | null): boolean {
    const isToday =
      !!syncAt && new Date(syncAt).toDateString() === new Date().toDateString();
    if (isToday || autoSyncTriggered.current || !stableFarmId) {
      return false;
    }
    autoSyncTriggered.current = true;
    autoSyncFarm({}, { farmId: stableFarmId });
    return true;
  }

  const handleSuccessFarmDataFetch = (farm: FarmsProps) => {
    const keysToMapToIds = new Set(['types', 'services', 'quality_marks']);
    const galleryUrls = farm.images.map((img: ImageProps) => img.url_original);
    const logoUrl = farm.logo;
    const publicAllowed = !!farm.public_allowed;

    setIsPublicAllowed(publicAllowed);

    if (logoUrl) {
      farmsLogoFetch({ path: logoUrl }, [], { logoUrl: logoUrl });
    }
    if (galleryUrls) {
      galleryUrls.forEach((url: string) =>
        farmsGalleryFetch({ path: url }, [], { galleryUrl: url })
      );
    }

    Object.entries(farm).forEach(([key, value]) => {
      if (key === 'public') {
        form.setFieldValue(key, publicAllowed ? value : false);
        return;
      }

      let finalValue = keysToMapToIds.has(key)
        ? value.map((item: { id: number }) => item.id)
        : value;

      if (key === 'contracts' && value.length) {
        form.setFieldValue('contract_id', value[0].id);
        return;
      }

      if (key === 'contract_date' && finalValue) {
        finalValue = dayjs(finalValue);
      }

      form.setFieldValue(key, finalValue);
    });

    triggerAutoSyncIfStale(farm.sync_at);
  };

  const items: TabsProps['items'] = [
    {
      key: 'base_information',
      label: intl.formatMessage({ id: 'navigation.company' }),
      children: (
        <BaseInformation isAdmin={true} publicAllowed={isPublicAllowed} />
      ),
    },
    {
      key: 'contact_information',
      label: intl.formatMessage({ id: 'navigation.contact_information' }),
      children: <ContactInformation farmId={stableFarmId} />,
    },
    {
      key: 'additional_information',
      label: intl.formatMessage({ id: 'navigation.additional_information' }),
      children: <AdditionalInformation />,
    },
    {
      key: 'business_gallery',
      label: intl.formatMessage({ id: 'navigation.gallery' }),
      children: (
        <BusinessGallery
          setFileLogo={setFileLogo}
          setFileGallery={setFileGallery}
          fileGallery={fileGallery}
          fileLogo={fileLogo}
        />
      ),
    },
    {
      key: 'business_addresses',
      label: intl.formatMessage({ id: 'navigation.business_addresses' }),
      children: (
        <BusinessAddresses
          farmId={stableFarmId}
          onInfrastructureChanged={() => handleSubmit()}
        />
      ),
    },
    {
      key: 'contracts',
      label: intl.formatMessage({ id: 'navigation.contracts' }),
      children: <Contracts farmId={stableFarmId} />,
    },
    {
      key: 'partners',
      label: intl.formatMessage({ id: 'navigation.partners' }),
      children: <Partners farmId={stableFarmId} />,
    },
    {
      key: 'deliveries',
      label: intl.formatMessage({ id: 'navigation.deliveries' }),
      children: <FarmDeliveries farmId={stableFarmId} />,
    },
    {
      key: 'quality_marks',
      label: intl.formatMessage({ id: 'navigation.quality_marks' }),
      children: <QualityMark stableFarmId={stableFarmId} />,
    },
  ];

  const changeStep = async (desiredStep: string) => {
    try {
      await form.validateFields();

      const index = items.findIndex((step) => step.key === desiredStep);

      if (index === -1) {
        return;
      }

      if (['contact_information', 'business_addresses'].includes(desiredStep)) {
        setIsFooterHidden(true);
      } else {
        setIsFooterHidden(false);
      }

      if (!isFooterHidden) {
        await handleSubmit();
      }
      setCurrentStep(index);
    } catch {}
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const formData = new FormData();
      const values = form.getFieldsValue();

      const rcFile = fileLogo[0]?.originFileObj;

      if (rcFile && rcFile instanceof Blob) {
        const realFile = new File([rcFile], rcFile.name, {
          type: rcFile.type,
          lastModified: rcFile.lastModified || Date.now(),
        });

        formData.append('logo_img', realFile);
      }

      if (fileGallery.length) {
        fileGallery.forEach((img: UploadFile) => {
          const galleryFile = img.originFileObj;

          if (galleryFile instanceof Blob) {
            const realFile = new File([galleryFile], galleryFile.name, {
              type: galleryFile.type,
              lastModified: galleryFile.lastModified || Date.now(),
            });

            formData.append('gallery[]', realFile, realFile.name);
          }
        });
      }

      Object.entries(values).forEach(([key, value]) => {
        if (
          key === 'gallery' ||
          key === 'logo' ||
          key === 'images' ||
          key === 'public_allowed'
        ) {
          return;
        }

        if (Array.isArray(value)) {
          value.forEach((item) => {
            formData.append(`${key}[]`, item);
          });
        } else if (value != null) {
          if (value !== null && typeof value === 'object') {
            formData.append(`${key}`, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        }
      });

      submitData(formData, { farmId: stableFarmId });
    } catch (e) {}
  };

  const Footer = () => {
    if (isFooterHidden) {
      return null;
    }
    return (
      <StyledBusinessInformationFooter>
        <div className={'footer'}>
          <Button
            label={intl.formatMessage({ id: 'general.cancel' })}
            onClick={() => setEditFarmDrawer()}
          />
          <Button
            label={intl.formatMessage({ id: 'general.save' })}
            onClick={() => {
              handleSubmit();
            }}
            type={'primary'}
          />
        </div>
      </StyledBusinessInformationFooter>
    );
  };

  const initialValues = {
    open_hours: convertToDayJs(options.open_hours),
  };
  const handleSyncFarm = () => {
    syncFarm({}, { farmId: stableFarmId });
  };

  return (
    <div>
      <StyledFarmEdit>
        <Spinner
          spinning={
            farmLoading || isFarmSaving || isLogoLoading || isGalleryLoading
          }
        >
          <Form form={form} layout={'vertical'} initialValues={initialValues}>
            <Tabs
              items={items}
              activeKey={items[currentStep].key}
              onChange={(desiredStep) => {
                changeStep(desiredStep);
              }}
              tabBarExtraContent={
                isGlobalAdmin && (
                  <Button
                    label={intl.formatMessage({ id: 'general.refresh_data' })}
                    type={'primary'}
                    onClick={handleSyncFarm}
                    loading={isFarmSyncing}
                    className="sync-farm-button"
                  />
                )
              }
            />
          </Form>
        </Spinner>
      </StyledFarmEdit>

      <DefaultLayout.PageFooter>
        <Footer />
      </DefaultLayout.PageFooter>
    </div>
  );
};
